import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CookieConsent } from "@/components/CookieConsent";
import {
  CinematicHero,
  SectionHead,
  Reveal,
  FullBleedQuote,
  SplitFeature,
  NumberedSteps,
  StatStripe,
  CinematicCTA,
  CinematicFAQ,
  TripleStrip,
} from "@/components/service/Cinematic";
import fassadeHero from "@/assets/fassade-hero.jpg";
import facadeRef1 from "@/assets/facade-ref-1.jpg";
import facadeRef2 from "@/assets/facade-ref-2.webp";
import facadeRef3 from "@/assets/facade-ref-6.jpg";
import facadeRef4 from "@/assets/facade-ref-4.jpg";
import facadeRef5 from "@/assets/facade-ref-5.jpg";
import facadeRef7 from "@/assets/facade-ref-7.jpg";
import facadeModern from "@/assets/facade-modern.jpg";

const systems = [
  {
    name: "EPS-Polystyrol",
    wls: "WLS 032–035",
    properties: "Bewährtes Standardsystem mit gutem Preis-Leistungs-Verhältnis und solider Dämmwirkung.",
    suitable: "Preisbewusste Sanierung, klassische Einfamilienhäuser",
    thickness: "80–200 mm",
  },
  {
    name: "Mineralwolle",
    wls: "A1 – nicht brennbar",
    properties: "Brandschutzklasse A1, schalldämmend und diffusionsoffen.",
    suitable: "Mehrgeschossige Gebäude, erhöhte Brandschutzanforderungen",
    thickness: "100–200 mm",
  },
  {
    name: "Resol / PIR",
    wls: "WLS 022",
    properties: "Höchste Dämmleistung pro Zentimeter – platzsparend und effizient.",
    suitable: "Denkmalschutz, knappe Platzverhältnisse",
    thickness: "60–120 mm",
  },
  {
    name: "Holzfaser",
    wls: "Ökologisch",
    properties: "Natürlich, diffusionsoffen, mit sommerlichem Hitzeschutz.",
    suitable: "Ökologisches Bauen, Holz- und Ferienhäuser",
    thickness: "100–240 mm",
  },
];

const steps = [
  { title: "Fassaden-Check vor Ort", desc: "Wir prüfen Putz, Dämmstärke, Feuchtigkeitsbild und Untergrund persönlich – ohne Berechnung." },
  { title: "Festpreis & Materialwahl", desc: "Schriftliches Angebot inklusive System-Empfehlung von Sto, Brillux oder Caparol und Förder-Hinweisen." },
  { title: "Gerüst & Vorbereitung", desc: "Aufbau, Untergrundvorbereitung, Abklebung und Schutz des direkten Umfelds." },
  { title: "Dämmung & Putzaufbau", desc: "Montage der Dämmplatten, Armierungsgewebe, Oberputz, Schlussanstrich – Schicht für Schicht in Meisterqualität." },
  { title: "Abnahme & Übergabe", desc: "Persönliche Abnahme mit Malermeister Stein, dokumentiert und besenrein." },
];

const faqs = [
  { q: "Was kostet eine Fassadensanierung mit WDVS in Herford?", a: "Die Kosten für ein WDVS liegen je nach System und Aufwand zwischen 120 und 250 € pro m² inkl. Montage, Putz und Anstrich. Ein Einfamilienhaus (ca. 150 m² Fassade) bewegt sich damit zwischen 18.000 und 37.500 €. Mit KfW- und BAFA-Förderung sind 15–20 % Ersparnis realistisch." },
  { q: "Wann ist der beste Zeitpunkt für die Fassadensanierung?", a: "April bis Oktober – sobald Temperaturen dauerhaft über 5 °C liegen und kein Frost zu erwarten ist. Planen Sie früh: gut gebuchte Betriebe haben im Frühjahr Wartelisten. Wintermonate eignen sich ideal für Beratung und Planung." },
  { q: "Kann ich eine Fassadendämmung fördern lassen?", a: "Ja. Für WDVS greift die KfW-BEG (bis 20 % Zuschuss) und die BAFA-Einzelmaßnahme. Voraussetzung ist ein Energieeffizienz-Experte. Wir koordinieren das mit Ihnen." },
  { q: "Wie lange hält ein fachgerecht ausgeführtes WDVS?", a: "30–40 Jahre. Entscheidend ist die Verarbeitungsqualität. Mit hochwertigen Systemen und sauberer Ausführung investieren Sie langfristig in Substanz und Energiebilanz." },
  { q: "Muss ich während der Sanierung zuhause sein?", a: "Nein. Nach Angebotsabnahme und Gerüstaufbau läuft Ihr Alltag normal weiter. Wir halten Sie täglich per WhatsApp auf dem Laufenden und melden uns persönlich zur Abnahme." },
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Fassadensanierung",
  name: "Fassadensanierung & WDVS Herford",
  provider: { "@type": "LocalBusiness", name: "Malerfachbetrieb Stein GmbH & Co. KG" },
  areaServed: "Herford",
  description: "Professionelle Fassadensanierung mit WDVS in Herford und Umgebung.",
};

const FassadensanierungWDVS = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Fassadensanierung & WDVS in Herford: Heizkosten senken"
      description="Fassadensanierung & WDVS in Herford: Wärmedämmung senkt Heizkosten bis 30 %, schützt Substanz & steigert den Wert. ✓ Kostenloser Fassaden-Check ☎ 05221 3832130"
      keywords="Fassadensanierung Herford, WDVS Herford, Wärmedämmverbundsystem Herford, Fassadendämmung Herford, Fassadenanstrich Herford, Algenstopp Fassade, Heizkosten senken Herford"
      canonical="https://malerfachbetrieb-stein.de/fassadensanierung-wdvs-herford"
      structuredData={structuredData}
    />
    <Navigation />
    <WhatsAppButton />

    <main>
      <CinematicHero
        chapter="Kapitel 01 / Fassade"
        eyebrow="Fassadensanierung & WDVS"
        title={<>Eine Fassade, die <em className="not-italic text-background/70">hält</em> – wenn der Sturm vorbei ist.</>}
        subtitle="Meistergeführte Fassadensanierung mit Wärmedämmverbundsystemen aus Herford. Wir schützen Ihre Bausubstanz, stoppen Algen und Risse und senken Heizkosten dauerhaft."
        image={fassadeHero}
        imageAlt="Fassadensanierung WDVS Herford – Malerfachbetrieb Stein"
        ctaLabel="Kostenlosen Fassaden-Check anfordern"
      />

      <StatStripe
        stats={[
          { value: "10+", label: "Jahre in Herford" },
          { value: "30 %", label: "Heizkosten gespart" },
          { value: "40", label: "Jahre WDVS-Lebensdauer" },
          { value: "24h", label: "Rückruf-Service" },
        ]}
      />

      {/* Intro split */}
      <section className="py-24 md:py-40">
        <div className="container mx-auto">
          <SplitFeature
            index="01"
            eyebrow="Wärmedämmverbundsystem"
            title="Eine durchdachte Hülle senkt den Verbrauch – nicht die Behaglichkeit."
            body={
              <>
                <p className="mb-4">
                  Ein vollständiges WDVS besteht aus Dämmplatten, Armierungsschicht und Oberputz – jede Lage trägt zur Langlebigkeit bei. Wir setzen Systeme von Sto, Brillux und Caparol ein: von wirtschaftlichem EPS bis zur platzsparenden Resol-Dämmung mit WLS 022.
                </p>
                <p>
                  Richtig ausgeführt reduziert ein WDVS Ihre Heizkosten um bis zu 30 % und schützt die Bausubstanz für Jahrzehnte.
                </p>
              </>
            }
            bullets={[
              "Systeme von Sto, Brillux & Caparol",
              "Dämmstärken 80–240 mm – passend zum Gebäude",
              "Lückenlose Detailausführung an Fenster & Sockel",
            ]}
            image={facadeRef4}
            imageAlt="WDVS-Sanierung Einfamilienhaus Herford"
          />
        </div>
      </section>

      <FullBleedQuote
        image={facadeModern}
        imageAlt="Moderne Fassade mit WDVS Detail"
        quote={<>„Eine Fassade ist die Visitenkarte des Hauses – aber zuerst ist sie sein Mantel.“</>}
        caption="Malermeister Stein, Herford"
      />

      {/* Was wir tun */}
      <section className="py-16 md:py-24 lg:py-32 bg-background">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Leistungen"
            title={<>Drei Disziplinen –<br /> eine Handschrift.</>}
            intro="Wir verbinden Sanierungserfahrung, präzise Detailarbeit und materialgerechte Verarbeitung in jedem Schritt."
          />
          <div className="space-y-24 md:space-y-32">
            <SplitFeature
              index="02"
              eyebrow="WDVS-System"
              title="Wärmedämmung, sauber gedacht."
              body="EPS, Mineralwolle oder Resol – die Wahl des Systems hängt von Brandschutz, Platz, Substanz und Budget ab. Wir analysieren und empfehlen, statt einen Standard zu verkaufen."
              image={facadeRef1}
              imageAlt="WDVS Detail Herford-Diebrock"
            />
            <SplitFeature
              reverse
              index="03"
              eyebrow="Fassadenanstrich"
              title="Putzreparatur & Schutzanstrich."
              body="Rissiger Altputz, ausgebleichte Farbe, Algenbefall: Wir analysieren den Zustand, sanieren gezielt und versiegeln die Fläche mit Silikonharzfarbe und Imprägnierschutz. Eine Fassade, die wieder Jahrzehnte hält."
              image={facadeRef2}
              imageAlt="Fassadenanstrich Hiddenhausen"
            />
            <SplitFeature
              index="04"
              eyebrow="Sockel & Feuchtigkeit"
              title="Der Bereich, der die Bausubstanz schützt."
              body="Sockel sind besonders beansprucht: Spritzwasser, Frost, aufsteigende Feuchte. Wir sanieren mit speziellen Sanierputzen und Abdichtungssystemen, die dauerhaft dicht halten."
              image={facadeRef3}
              imageAlt="Sockelsanierung Enger"
            />
          </div>
        </div>
      </section>

      {/* Systeme */}
      <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
        <div className="container mx-auto">
          <SectionHead
            invert
            eyebrow="Systeme & Materialien"
            title={<>Welches System passt zu Ihrem Haus?</>}
            intro="Vier Dämmstoff-Familien decken nahezu jede Aufgabe ab. Die Wahl entscheidet über Wirkung, Optik, Brandschutz und Förderfähigkeit."
          />
          <div className="grid md:grid-cols-2 gap-px bg-background/10">
            {systems.map((sys, i) => (
              <Reveal key={sys.name} delay={i * 80}>
                <div className="bg-foreground p-8 md:p-10 h-full">
                  <div className="flex items-baseline justify-between mb-6">
                    <h3 className="font-caslon text-2xl md:text-3xl font-bold text-background">{sys.name}</h3>
                    <span className="text-[10px] tracking-[0.25em] uppercase text-background/80">{sys.wls}</span>
                  </div>
                  <p className="text-background/70 leading-relaxed mb-6 text-sm">{sys.properties}</p>
                  <div className="grid grid-cols-2 gap-4 text-xs pt-4 border-t border-background/15">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-background/75 mb-1">Geeignet für</div>
                      <div className="text-background/85">{sys.suitable}</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-background/75 mb-1">Stärke</div>
                      <div className="text-background/85">{sys.thickness}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="py-16 md:py-24 lg:py-32 bg-background">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Ablauf"
            title="So entsteht Ihre neue Fassade."
            intro="Fünf Schritte, klar getaktet – vom ersten Besuch bis zur dokumentierten Übergabe."
          />
          <NumberedSteps steps={steps} />
        </div>
      </section>

      <TripleStrip
        images={[
          { src: facadeRef5, alt: "Fassadenprojekt Herford", caption: "WDVS – Einfamilienhaus" },
          { src: facadeRef7, alt: "Fassadenprojekt Hiddenhausen", caption: "Fassaden­anstrich – Altbau" },
          { src: facadeRef3, alt: "Fassadenprojekt Enger", caption: "Sockel­sanierung mit Abdichtung" },
        ]}
      />

      <CinematicFAQ
        faqs={faqs}
        title="Fragen, die uns oft erreichen."
      />

      <CinematicCTA
        image={fassadeHero}
        imageAlt="Fassadensanierung Herford"
        title={<>Bereit für eine Fassade,<br /> die wieder trägt?</>}
        body="Kostenloser Fassaden-Check vor Ort. Unverbindlich, mit Festpreisangebot und Förder-Hinweisen."
        ctaLabel="Fassaden-Check anfordern"
      />
    </main>

    <Footer />
    <CookieConsent />
  </div>
);

export default FassadensanierungWDVS;